import React, { useCallback, useRef } from 'react';
import { Icon } from '@dotcom/crown-ui';
import { ControlButton } from '../../control-button/control-button';
import { ToolbarHeader } from './fragments/toolbar-header';
import { ToolbarAction, ToolbarActionMenuProps } from './toolbar-action-menu.types';
import styles from './toolbar-action-menu.module.scss';

const DEFAULT_TEST_ID_PREFIX = 'toolbar-action-menu';

const actionTestId = (action: ToolbarAction): string => action.testId ?? `drawer-button-${action.label}`;

const ActionRow = ({ actions }: { actions: ToolbarAction[] }): React.ReactElement => (
  <div className={styles.menu}>
    {actions.map((action) => (
      <ControlButton
        key={action.id}
        testId={actionTestId(action)}
        label={action.label}
        buttonClass={styles.button}
        onClick={action.onClick}
        ariaLabel={action.ariaLabel ?? action.label}
      >
        <Icon name={action.icon} size="24" className={styles.icon} color="black" />
      </ControlButton>
    ))}
  </div>
);

/**
 * Reusable drawer-style toolbar panel: chrome (white background, rounded
 * border, drop shadow, slide-in animation), header with Back / Close
 * buttons, and a body that defaults to a horizontal row of action buttons.
 *
 * Feature code provides the list of actions or its own inline body
 * (`children`). All chrome, animation, header layout and accessibility
 * affordances live here so the menu is reusable across drawers (image edit
 * toolbar, font/text drawer, etc.).
 */
export const ToolbarActionMenu = ({
  ariaLabel,
  backLabel,
  closeLabel,
  onBack,
  onClose,
  showBack,
  title,
  actions,
  children,
  variant = 'floating',
  slideDirection = 'left-to-right',
  className,
  hideHeader,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  isClosing,
  onExitComplete,
}: ToolbarActionMenuProps): React.ReactElement => {
  const variantClass = variant === 'floating' ? styles['panel-floating'] : styles['panel-embedded'];
  const slideClass = (() => {
    if (isClosing) {
      return slideDirection === 'right-to-left' ? styles['slide-out-right'] : styles['slide-out-left'];
    }
    return slideDirection === 'right-to-left' ? styles['slide-right-to-left'] : styles['slide-left-to-right'];
  })();
  const rootClassName = [styles.panel, variantClass, slideClass, className].filter(Boolean).join(' ');

  const handleAnimationEnd = useCallback(() => {
    if (isClosing && onExitComplete) {
      onExitComplete();
    }
  }, [isClosing, onExitComplete]);

  // Freeze body content during exit animation so the panel fades out
  // showing whatever was last visible, instead of flashing the default
  // action row when activeTool is reset to null.
  const bodyContent = children ?? <ActionRow actions={actions ?? []} />;
  const cachedBodyRef = useRef<React.ReactNode>(bodyContent);
  if (!isClosing) {
    cachedBodyRef.current = bodyContent;
  }

  // Similarly freeze header state so back/title doesn't jump during close.
  const cachedShowBackRef = useRef(showBack);
  if (!isClosing) {
    cachedShowBackRef.current = showBack;
  }

  return (
    <div
      className={rootClassName}
      role="toolbar"
      aria-label={ariaLabel}
      data-testid={`${testIdPrefix}-panel`}
      onAnimationEnd={handleAnimationEnd}
    >
      <span className={styles['hidden-label']}>{ariaLabel}</span>
      {!hideHeader && (
        <div className={styles['panel-header-row']}>
          <ToolbarHeader
            backLabel={backLabel}
            closeLabel={closeLabel}
            onBack={onBack}
            onClose={onClose}
            showBack={cachedShowBackRef.current}
            title={title}
            backTestId={`${testIdPrefix}-back`}
            closeTestId={`${testIdPrefix}-close`}
          />
        </div>
      )}
      <div className={hideHeader ? `${styles['panel-body']} ${styles['panel-body--no-header']}` : styles['panel-body']}>
        {cachedBodyRef.current}
      </div>
    </div>
  );
};
